import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from 'antd';

interface DimensionToggleProps {
    map: any;
    is3D?: boolean;
    onToggle?: (is3D: boolean) => void;
}

/**
 * DimensionToggle Component
 * Provides a 2D/3D toggle for Mapbox GL JS with terrain support.
 *
 * Uses a ref for is3D state so that style.load event handlers always
 * read the live value — avoids stale closure after style switches.
 */
const DimensionToggle: React.FC<DimensionToggleProps> = ({ map, is3D: controlledIs3D, onToggle }) => {
    const [internalIs3D, setInternalIs3D] = useState(false);
    const is3D = controlledIs3D !== undefined ? controlledIs3D : internalIs3D;
    
    const setIs3D = (val: boolean) => {
        if (onToggle) onToggle(val);
        setInternalIs3D(val);
    };


    // Ref that always holds the current 3D state — safe to read from event callbacks
    const is3DRef = useRef(false);

    // Keep the ref in sync whenever state changes
    useEffect(() => {
        is3DRef.current = is3D;
    }, [is3D]);

    /**
     * Set up a natural sunny blue sky atmosphere.
     * Improves contrast and visibility for map controls.
     */
    const setupAtmosphere = useCallback(() => {
        if (!map) return;
        
        if (!map.isStyleLoaded()) {
            map.once('styledata', setupAtmosphere);
            return;
        }

        map.setFog({
            'range': [0.8, 8],
            'color': '#CCEFFF', // Sunny Sky Blue
            'horizon-blend': 0.05,
            'high-color': '#EBF5FF', // Very light blue for the upper atmosphere
            'space-color': '#000000',
            'star-intensity': 0
        });
    }, [map]);

    /**
     * Add the mapbox-dem raster-dem source if it isn't already present.
     * Safe to call multiple times.
     */
    const setupTerrain = useCallback(() => {
        if (!map) return;

        if (!map.isStyleLoaded()) {
            map.once('styledata', setupTerrain);
            return;
        }

        if (!map.getSource('mapbox-dem')) {
            map.addSource('mapbox-dem', {
                type: 'raster-dem',
                url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
                tileSize: 512,
                maxzoom: 14,
            });
        }
    }, [map]);

    // React to prop changes from parent (e.g. Back button reset)
    useEffect(() => {
        if (!map || controlledIs3D === undefined) return;

        const applyState = () => {
            if (!map.isStyleLoaded()) {
                map.once('styledata', applyState);
                return;
            }

            if (controlledIs3D) {
                if (!map.getSource('mapbox-dem')) {
                    setupTerrain();
                }
                if (map.getSource('mapbox-dem')) {
                    map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
                }
                setupAtmosphere();
            } else {
                map.setTerrain(null);
                map.setFog(null);
            }
        };

        applyState();
    }, [controlledIs3D, map, setupTerrain, setupAtmosphere]);

    /**
     * Re-add the DEM source and, if 3D was active, re-apply terrain.
     * Called every time a new style finishes loading (style.load).
     */
    const onStyleLoad = useCallback(() => {
        setupTerrain();

        // Read from ref — never stale even if this handler was registered
        // before the last setIs3D call.
        if (is3DRef.current) {
            // Small defer to let the style fully settle before applying terrain/fog
            setTimeout(() => {
                if (map) {
                    if (map.getSource('mapbox-dem')) {
                        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
                    }
                    setupAtmosphere();
                }
            }, 50);
        }
    }, [map, setupTerrain]);

    useEffect(() => {
        if (!map) return;

        // If the style is already loaded when this mounts (e.g. map ref arrived
        // after initial load), set up immediately.
        if (map.isStyleLoaded()) {
            setupTerrain();
        }

        map.on('style.load', onStyleLoad);

        return () => {
            map.off('style.load', onStyleLoad);
        };
    }, [map, setupTerrain, onStyleLoad]);

    const toggleDimension = () => {
        if (!map) return;

        if (is3D) {
            // Switch back to 2D — flatten pitch and remove terrain
            map.easeTo({
                pitch: 0,
                bearing: 0,
                duration: 1000,
                essential: true,
            });
            map.setTerrain(null);
            map.setFog(null); // Clear sunny atmosphere in 2D
            setIs3D(false);
        } else {
            // Switch to 3D — tilt map and activate terrain
            map.easeTo({
                pitch: 60,
                bearing: -20,
                duration: 1000,
                essential: true,
            });

            // Ensure DEM source exists before activating terrain
            if (!map.getSource('mapbox-dem')) {
                setupTerrain();
            }
            if (map.getSource('mapbox-dem')) {
                map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
            }
            setupAtmosphere(); // Apply sunny atmosphere
            setIs3D(true);
        }
    };

    if (!map) return null;

    return (
        <div
            className="mapboxgl-ctrl mapboxgl-ctrl-group dimension-toggle-ctrl"
            style={{
                position: 'absolute',
                // 2D/3D toggle placed below the compass, same size as the mapbox control buttons, at the
                // same 24px distance they keep between them (see `@map-control-gap` in `map.less`)
                top: '198px',
                right: '10px',
                zIndex: 10,
                margin: 0,
                pointerEvents: 'auto',
            }}
        >
            <Button
                onClick={toggleDimension}
                // The same blue press flash the zoom buttons get (see `map-control-touch` in `map.less`)
                onPointerDown={(event) => {
                    const element = event.currentTarget;
                    element.classList.remove('map-control-touched');
                    void element.offsetWidth;
                    element.classList.add('map-control-touched');
                }}
                onAnimationEnd={(event) => event.currentTarget.classList.remove('map-control-touched')}
                style={{
                    width: '35px',
                    height: '35px',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '17px',
                    fontWeight: 'bold',
                    fontFamily: 'Arial, "Liberation Sans", "Nimbus Sans", Roboto, sans-serif',
                    paddingTop: '1.5px',
                    backgroundColor: 'transparent',
                    color: '#333',
                    border: 'none',
                    borderRadius: 'inherit',
                    boxShadow: 'none',
                }}
                className="dimension-toggle-btn"
            >
                {is3D ? '2D' : '3D'}
            </Button>
        </div>
    );
};

export default DimensionToggle;